import { useThrottle } from '@core/shared';
import { joinChatChannel, useAppDispatch, useAppSelector } from '@online-webinar/store';
import { useEffect, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { ParticipantItem } from '../../../types';
import { ParticipantRow } from '../ParticipantRow';

export const ParticipantListRows = ({ search }: { search: string }) => {
    const dispatch = useAppDispatch();
    const chatChannels = useAppSelector((state) => state.liveWebinar.chatChannels);
    const throttledChatChannels = useThrottle(chatChannels, 1000);
    const participantSessions = useAppSelector((state) => state.liveWebinar.participants);
    const [participants, setParticipants] = useState<ParticipantItem[]>([]);

    useEffect(() => {
        // Add chat count to each participant
        let arr: ParticipantItem[] = participantSessions.map((participant) => {
            return {
                ...participant,
                unreadTimestamp: chatChannels[participant.participantId]?.unreadTimestamp,
                noOfUnreadMessages: chatChannels[participant.participantId]?.noOfUnreadMessages || 0
            };
        });

        // Deduplicate participants based on participantId
        arr = arr.reduce((acc, current) => {
            const x = acc.find((item) => item.participantId === current.participantId);
            if (!x) {
                return acc.concat([current]);
            } else {
                return acc;
            }
        }, [] as ParticipantItem[]);

        // Filter participants based on search
        if (search) {
            arr = arr.filter((participant) => {
                return (participant.nickname ?? '').toLowerCase().includes(search.toLowerCase());
            });
        }

        // Sort participants by chat count and then by nickname
        arr.sort((a, b) => {
            if (a.unreadTimestamp && b.unreadTimestamp) {
                const aDate = new Date(a.unreadTimestamp);
                const bDate = new Date(b.unreadTimestamp);
                return bDate.getTime() - aDate.getTime();
            }

            if (a.unreadTimestamp) {
                return -1;
            }

            if (b.unreadTimestamp) {
                return 1;
            }

            return (a.nickname ?? '').localeCompare(b.nickname ?? '');
        });
        setParticipants(arr);
    }, [participantSessions, throttledChatChannels, search]);

    const onParticipantClick = (participantId: string) => {
        dispatch(joinChatChannel(participantId));
    };

    return (
        <Virtuoso
            initialTopMostItemIndex={999}
            data={participants}
            itemContent={(_, participant) => <ParticipantRow participant={participant} onParticipantClick={onParticipantClick} />}
            followOutput="auto"
            className="min-h-24 overflow-x-hidden overscroll-contain"
        />
    );
};
